
import { Subscription } from '@/types/subscription';
import { formatDateForCsv } from '@/utils/dateFormatters';
import { fetchSubscriptionsFromSupabase } from "../core/subscriptionApi";
import { getLocalSubscriptions } from "@/utils/localStorageUtils";
import { checkSupabaseClient } from "@/utils/supabaseUtils";

export const exportSubscriptionsAsCSV = async (): Promise<Blob> => {
  console.log("=== CSV EXPORT DEBUG ===");
  console.log("exportSubscriptionsAsCSV called");
  
  try {
    let subscriptions: Subscription[] = [];
    
    // Try to get data from Supabase first
    if (checkSupabaseClient()) {
      try {
        subscriptions = await fetchSubscriptionsFromSupabase();
        console.log("Got subscription data from Supabase for export:", subscriptions);
        console.log("Business subscriptions found:", subscriptions.filter(s => s.isBusiness));
      } catch (err) {
        console.error("Error fetching from Supabase for export:", err);
        // Continue to local storage fallback
      }
    }
    
    // Fall back to local storage if no Supabase data
    if (subscriptions.length === 0) {
      console.log("Using local storage for export");
      subscriptions = getLocalSubscriptions();
      console.log("Local storage business subscriptions:", subscriptions.filter(s => s.isBusiness));
    }
    
    // Filter out only cancelled subscriptions from the export
    const activeSubscriptions = subscriptions.filter(sub => 
      sub.status !== 'cancelled'
    );
    
    console.log("Active subscriptions for export:", activeSubscriptions.length);
    console.log("Active business subscriptions:", activeSubscriptions.filter(s => s.isBusiness));
    
    if (!activeSubscriptions || activeSubscriptions.length === 0) {
      console.log("No active subscription data to export");
      // Return an empty CSV with headers if no data exists
      const headers = ['Name', 'Email', 'Address', 'Postcode', 'Bin Day', 'Start Date', 'Subscription Type', 'Is Business', 'Business Name', 'Multiple Days', 'Total Paid'];
      return new Blob([headers.join(',')], { type: 'text/csv;charset=utf-8;' });
    }
    
    return generateCSVBlob(activeSubscriptions);
  } catch (error) {
    console.error("Failed to export subscriptions:", error);
    throw new Error("Failed to export subscriptions: " + (error instanceof Error ? error.message : String(error)));
  }
};

const generateCSVBlob = (subscriptions: Subscription[]): Blob => {
  console.log("Generating CSV for subscriptions:", subscriptions.length);
  console.log("Business subscriptions in CSV generation:", subscriptions.filter(s => s.isBusiness));
  
  const csvData = subscriptions.map(sub => {
    // Calculate correct pricing based on business and annual status
    let subscriptionTypeText = '';
    if (sub.isAnnual) {
      if (sub.isBusiness) {
        subscriptionTypeText = 'Business Annual (£840/year)';
      } else {
        subscriptionTypeText = 'Annual (£210/year)';
      }
    } else {
      if (sub.isBusiness) {
        subscriptionTypeText = 'Business Weekly (£20/week)';
      } else {
        subscriptionTypeText = 'Weekly (£5/week)';
      }
    }

    const csvRow = {
      'Name': sub.customerName || '',
      'Email': sub.email || '',
      'Address': sub.address || '',
      'Postcode': sub.postcode || '',
      'Bin Day': sub.binDay || '',
      'Start Date': formatDateForCsv(sub.startDate),
      'Subscription Type': subscriptionTypeText,
      'Is Business': sub.isBusiness ? 'Yes' : 'No',
      'Business Name': sub.isBusiness ? (sub.businessName || 'Not specified') : 'N/A',
      'Multiple Days': sub.multipleDays ? 'Yes' : 'No',
      'Total Paid': `£${(sub.totalPaid || 0).toFixed(2)}`
    };
    
    console.log("CSV row for subscription:", {
      name: csvRow.Name,
      isBusiness: csvRow['Is Business'],
      businessName: csvRow['Business Name'],
      subscriptionType: csvRow['Subscription Type']
    });
    
    return csvRow;
  });

  const headers = Object.keys(csvData[0]);
  
  const csvString = [
    headers.join(','),
    ...csvData.map(item => {
      return headers.map(header => {
        const value = String(item[header as keyof typeof item] || '');
        // Escape commas and quotes in the data
        return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(',');
    })
  ].join('\n');

  console.log("Final CSV string contains business subscriptions:", csvString.includes('Business'));
  console.log("=== END CSV EXPORT DEBUG ===");
  return new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
};
